<template>
  <v-card elevation="4" rounded="lg" outlined class="sala-estudos-card">
    <v-card-title class="gradient-header white--text py-4">
      <v-icon left color="white" size="24">mdi-account-group</v-icon>
      <span class="text-h6">Sala de Estudos</span>
      <v-spacer></v-spacer>
      <v-chip
          v-if="salaEstudos"
          color="white"
          text-color="primary"
          small
          class="font-weight-bold"
      >
        <v-icon left small>mdi-account-multiple</v-icon>
        {{ salaEstudos.totalUsuariosEstudando }}
      </v-chip>
    </v-card-title>

    <v-card-text class="pa-4">
      <!-- Loading State -->
      <div v-if="carregando" class="text-center py-8">
        <v-progress-circular
            indeterminate
            color="primary"
            size="48"
        ></v-progress-circular>
        <div class="text-body-2 grey--text mt-3">Carregando sala de estudos...</div>
      </div>

      <!-- Empty State -->
      <div v-else-if="!salaEstudos || salaEstudos.totalUsuariosEstudando === 0" class="text-center py-8">
        <v-icon size="64" color="grey lighten-1">mdi-account-off-outline</v-icon>
        <div class="text-h6 grey--text mt-3">Ninguém estudando no momento</div>
        <div class="text-body-2 grey--text mt-2">Seja o primeiro a começar! 🚀</div>
      </div>

      <!-- Users List -->
      <div v-else>
        <!-- Header Info -->
        <div v-if="salaEstudos.voceEstaEstudando" class="mb-4">
          <v-alert
              type="success"
              dense
              text
              border="left"
              class="mb-0"
              icon="mdi-check-circle"
          >
            <span class="font-weight-medium">Você está estudando agora!</span>
          </v-alert>
        </div>

        <!-- Users Cards -->
        <div class="usuarios-container" style="max-height: 400px; overflow-y: auto;">
          <v-card
              v-for="usuario in salaEstudos.usuariosEstudando"
              :key="usuario.userId"
              class="mb-3 usuario-card"
              elevation="2"
              outlined
          >
            <v-card-text class="pa-3">
              <v-row align="center" no-gutters>
                <!-- Avatar -->
                <v-col cols="auto">
                  <v-avatar
                      :color="getAvatarColor(usuario.userId)"
                      size="48"
                      class="white--text font-weight-bold"
                  >
                    {{ getInitials(usuario.nomeUsuario) }}
                  </v-avatar>
                </v-col>

                <!-- User Info -->
                <v-col class="pl-3">
                  <div class="d-flex align-center mb-1">
    <span class="text-subtitle-1 font-weight-medium">
      {{ usuario.nomeUsuario }}
    </span>
                    <v-chip
                        v-if="ehUsuarioAtual(usuario.userId)"
                        x-small
                        color="success"
                        dark
                        class="ml-2"
                    >
                      Você
                    </v-chip>
                  </div>

                  <div class="d-flex align-center mb-1">
                    <v-icon small color="primary" class="mr-1">mdi-book-open-variant</v-icon>
                    <span class="text-body-2 grey--text text--darken-2">
      {{ usuario.temaEstudo }}
    </span>
                  </div>

                  <div v-if="usuario.observacao" class="d-flex align-center mb-1">
                    <v-icon small color="grey" class="mr-1">mdi-note-text-outline</v-icon>
                    <span class="text-caption grey--text text--darken-1">
      {{ usuario.observacao }}
    </span>
                  </div>

                  <div class="d-flex align-center">
                    <v-icon small color="grey" class="mr-1">mdi-clock-outline</v-icon>
                    <span class="text-caption grey--text">
      Estudo iniciado há {{ formatarTempo(usuario.tempoDecorridoMinutos) }}
    </span>
                    <v-spacer></v-spacer>
                    <v-chip
                        x-small
                        :color="getTempoEstudadoColor(calcularTempoEstudadoHoje(usuario))"
                        dark
                    >
                      <v-icon x-small left>mdi-fire</v-icon>
                      {{ formatarTempoEstudadoHoje(calcularTempoEstudadoHoje(usuario)) }}
                    </v-chip>
                  </div>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </div>

        <!-- Footer Stats -->
        <v-divider class="my-3"></v-divider>
        <div class="text-center">
          <div class="text-caption grey--text">
            <v-icon small color="grey">mdi-information-outline</v-icon>
            Atualizado {{ ultimaAtualizacao }}
          </div>
        </div>
      </div>
    </v-card-text>
  </v-card>
</template>

<script>
import estudoService from '@/services/estudoService';
import authService from '@/services/authService';

export default {
  name: 'SalaEstudos',

  data() {
    return {
      salaEstudos: null,
      carregando: false,
      intervalId: null,
      timestampInterval: null,
      tempoAtualInterval: null,
      ultimaAtualizacao: 'agora',
      ultimaAtualizacaoTime: null,
      tempoAtual: Date.now(),
      cores: [
        '#FF6B6B', '#4ECDC4', '#45B7D1', '#FFA07A',
        '#98D8C8', '#F7DC6F', '#BB8FCE', '#85C1E2',
        '#F8B739', '#52B788', '#E76F51', '#2A9D8F'
      ]
    };
  },

  mounted() {
    this.carregarSalaEstudos();
    this.iniciarAtualizacaoAutomatica();
    document.addEventListener('visibilitychange', this.handleVisibilityChange);
  },

  beforeDestroy() {
    document.removeEventListener('visibilitychange', this.handleVisibilityChange);
    this.pararAtualizacaoAutomatica();
  },

  methods: {
    async carregarSalaEstudos() {
      if (this.isDocumentHidden()) return;

      try {
        this.carregando = true;
        this.salaEstudos = await estudoService.obterSalaEstudos();
        this.atualizarTimestamp();
      } catch (error) {
        console.error('Erro ao carregar sala de estudos:', error);
        this.$toast.error('Erro ao carregar sala de estudos');
      } finally {
        this.carregando = false;
      }
    },

    iniciarAtualizacaoAutomatica() {
      this.pararAtualizacaoAutomatica();
      if (this.isDocumentHidden()) return;

      this.tempoAtual = Date.now();

      // Atualiza a cada 30 segundos
      this.intervalId = setInterval(() => {
        this.carregarSalaEstudos();
      }, 30000);

      // Atualiza o timestamp a cada segundo
      this.timestampInterval = setInterval(() => {
        if (this.isDocumentHidden()) return;
        this.atualizarTimestamp();
      }, 1000);

      // Atualiza o tempo atual a cada segundo para recalcular o tempo estudado
      this.tempoAtualInterval = setInterval(() => {
        if (this.isDocumentHidden()) return;
        this.tempoAtual = Date.now();
      }, 1000);
    },

    pararAtualizacaoAutomatica() {
      if (this.intervalId) {
        clearInterval(this.intervalId);
        this.intervalId = null;
      }
      if (this.timestampInterval) {
        clearInterval(this.timestampInterval);
        this.timestampInterval = null;
      }
      if (this.tempoAtualInterval) {
        clearInterval(this.tempoAtualInterval);
        this.tempoAtualInterval = null;
      }
    },

    isDocumentHidden() {
      return typeof document !== 'undefined' && document.hidden;
    },

    handleVisibilityChange() {
      if (this.isDocumentHidden()) {
        this.pararAtualizacaoAutomatica();
        return;
      }

      this.tempoAtual = Date.now();
      this.atualizarTimestamp();
      this.carregarSalaEstudos();
      this.iniciarAtualizacaoAutomatica();
    },

    atualizarTimestamp() {
      const agora = new Date();

      if (!this.ultimaAtualizacaoTime) {
        this.ultimaAtualizacao = 'agora';
        this.ultimaAtualizacaoTime = agora;
        return;
      }

      const segundos = Math.floor((agora - this.ultimaAtualizacaoTime) / 1000);

      if (segundos < 5) {
        this.ultimaAtualizacao = 'agora';
      } else if (segundos < 60) {
        this.ultimaAtualizacao = `há ${segundos}s`;
      } else {
        const minutos = Math.floor(segundos / 60);
        this.ultimaAtualizacao = `há ${minutos}min`;
      }
    },

    getInitials(nome) {
      if (!nome) return '?';
      const parts = nome.trim().split(' ');
      if (parts.length === 1) {
        return parts[0].substring(0, 2).toUpperCase();
      }
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    },

    getAvatarColor(userId) {
      // Gera uma cor consistente baseada no userId
      return this.cores[userId % this.cores.length];
    },

    getTempoColor(minutos) {
      if (minutos < 15) return 'grey';
      if (minutos < 30) return 'blue';
      if (minutos < 60) return 'orange';
      return 'red';
    },

    getTempoEstudadoColor(segundos) {
      const minutos = Math.floor(segundos / 60);
      if (minutos < 15) return 'grey';
      if (minutos < 30) return 'blue';
      if (minutos < 60) return 'orange';
      return 'red';
    },

    formatarTempo(minutos) {
      if (minutos < 60) {
        return `${minutos} minutos`;
      }
      const horas = Math.floor(minutos / 60);
      const mins = minutos % 60;
      if (mins === 0) {
        return `${horas}h`;
      }
      return `${horas}h ${mins}min`;
    },

    calcularTempoEstudadoHoje(usuario) {
      if (!usuario) return 0;

      let segundos = usuario.tempoEstudadoHoje || 0;

      // APENAS soma tempo atual se estiver EM_ANDAMENTO e tiver ultimoInicio
      if (usuario.status === 'EM_ANDAMENTO' && usuario.ultimoInicio) {
        const inicio = new Date(usuario.ultimoInicio).getTime();
        const segundosAtuais = Math.floor((this.tempoAtual - inicio) / 1000);
        segundos += segundosAtuais;
      }

      return segundos;
    },

    formatarTempoEstudadoHoje(segundos) {
      if (!segundos || segundos === 0) return '0min';

      const horas = Math.floor(segundos / 3600);
      const minutos = Math.floor((segundos % 3600) / 60);

      if (horas === 0) {
        return `${minutos}min`;
      }
      if (minutos === 0) {
        return `${horas}h`;
      }
      return `${horas}h${minutos}min`;
    },

    ehUsuarioAtual(userId) {
      const tokenPayload = authService.token.decode();
      return tokenPayload && tokenPayload.userId === userId;
    },

    // Método público para refresh manual
    refresh() {
      this.carregarSalaEstudos();
    }
  }
};
</script>

<style scoped>
.sala-estudos-card {
  transition: all 0.3s ease;
  border: 1px solid rgba(0, 0, 0, 0.08);
}

.gradient-header {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
}

.usuario-card {
  transition: all 0.2s ease;
  border-left: 3px solid transparent;
}

@media (hover: hover) {
  .usuario-card:hover {
    transform: translateX(4px);
    border-left-color: #667eea;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1) !important;
  }
}

@media (max-width: 599px) {
  .sala-estudos-card,
  .usuario-card {
    transition: none !important;
  }

  .v-avatar {
    box-shadow: none;
  }
}

.usuarios-container::-webkit-scrollbar {
  width: 6px;
}

.usuarios-container::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 10px;
}

.usuarios-container::-webkit-scrollbar-thumb {
  background: #888;
  border-radius: 10px;
}

.usuarios-container::-webkit-scrollbar-thumb:hover {
  background: #555;
}

.v-avatar {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}
</style>
